import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const ACAZDA_ORIGIN = "https://www.acadza.com";

function setCookie(name: string, value: string, days: number) {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = "expires=" + d.toUTCString();

    const isHttps = window.location.protocol === "https:";
    document.cookie =
        `${name}=${encodeURIComponent(value)};` +
        `${expires};path=/;${isHttps ? "Secure;" : ""}SameSite=Lax`;
}

function getCookie(name: string) {
    const cname = name + "=";
    const decoded = decodeURIComponent(document.cookie || "");
    const parts = decoded.split(";");

    for (let i = 0; i < parts.length; i++) {
        const c = parts[i].trim();
        if (c.indexOf(cname) === 0) return c.substring(cname.length);
    }
    return "";
}

function deleteCookie(name: string) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

export default function DostClient() {
    const navigate = useNavigate();
    const [showPopup, setShowPopup] = useState(true);
    const [loginId, setLoginId] = useState("");
    const [password, setPassword] = useState("");
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [iframeSrc, setIframeSrc] = useState<string | null>(null);

    const loginUrl = useMemo(() => `${ACAZDA_ORIGIN}/login?ref=scubus`, []);

    const signupUrl = (alpha: string, beta: string) =>
        `${ACAZDA_ORIGIN}/login?alpha=${encodeURIComponent(alpha)}&beta=${encodeURIComponent(beta)}`;

    const loadLoginIframe = () => {
        setIframeSrc(loginUrl);
    };

    const loadSignupIframe = (alpha: string, beta: string) => {
        setIframeSrc(signupUrl(alpha, beta));
    };

    const saveData = () => {
        const phone = loginId.trim();
        const pass = password.trim();

        setErrorMsg(null);

        if (!phone || !pass) {
            setErrorMsg("Please enter login id and password.");
            return;
        }

        setCookie("phone", phone, 7);
        setCookie("password", pass, 7);

        loadSignupIframe(phone, pass);
        setShowPopup(false);
    };

    useEffect(() => {
        // Always show login prompt when opening /dost; prefill if cookies exist.
        const phone = getCookie("phone");
        const pass = getCookie("password");
        setLoginId(phone);
        setPassword(pass);
        loadLoginIframe();
        setShowPopup(true);
    }, []);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            // Security: only accept messages from acadza.com
            if (event.origin !== ACAZDA_ORIGIN) return;

            const action = (event.data as any)?.action;

            if (action === "logout") {
                deleteCookie("phone");
                deleteCookie("password");
                loadLoginIframe();
                setShowPopup(true);
                setErrorMsg(null);
                setPassword("");
            }

            if (action === "loginerror") {
                deleteCookie("phone");
                deleteCookie("password");
                loadLoginIframe();
                setShowPopup(true);
                setErrorMsg("Login failed. Please check your credentials.");
                setPassword("");
            }
        };

        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, []);

    return (
        <div className="dost-root">
            {/* Popup */}
            {showPopup ? (
                <div className="popup">
                    <div className="popupContent">
                        <button
                            className="popupClose"
                            aria-label="Close"
                            onClick={() => navigate("/")}
                        >
                            ×
                        </button>
                        <img className="popupLogo" src="/logo/logo.png" alt="S-CUBUS logo" />
                        <h3>Enter Details</h3>
                        <div className="popupBody">
                            <input
                                type="text"
                                placeholder="Login ID"
                                value={loginId}
                                onChange={(e) => setLoginId(e.target.value)}
                            />

                            <input
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") saveData();
                                }}
                            />

                            {errorMsg ? <div className="error">{errorMsg}</div> : null}
                        </div>
                        <button className="popupSubmit" onClick={saveData}>
                            Submit
                        </button>
                    </div>
                </div>
            ) : null}

            {/* Iframe: only render after popup closes so no background page is visible */}
            {!showPopup && iframeSrc && (
                <iframe
                    className="frame"
                    src={iframeSrc}
                    allowFullScreen
                    title="S-Cubus Dost"
                />
            )}


            <style>{`
        .dost-root {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100vh;
          font-family: Arial, sans-serif;
        }

        .frame {
          border: none;
          width: 100%;
          height: 100%;
          display: block;
        }

        .popup {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: #ffffff;
          display: flex;
          align-items: stretch;
          justify-content: stretch;
          z-index: 9999;
        }

        .popupContent {
          background: #fff;
          padding: 32px 24px 20px;
          border-radius: 0;
          width: 100%;
          min-height: 100dvh;
          box-shadow: none;
          text-align: center;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 14px;
          overflow-y: auto;
        }

        .popupContent h3 {
          margin: 0;
          font-size: clamp(30px, 4vw, 52px);
          line-height: 1.1;
          color: #18324e;
        }

        .popupLogo {
          width: clamp(96px, 12vw, 160px);
          height: auto;
          margin: 0 auto 6px;
          display: block;
        }

        .popupBody {
          width: min(560px, 100%);
          margin: 0 auto;
        }

        .popupContent input {
          width: 100%;
          padding: 14px 12px;
          margin: 10px 0;
          border: 1px solid #ccc;
          border-radius: 8px;
          outline: none;
          font-size: 17px;
        }

        .popupSubmit {
          background: #007bff;
          color: white;
          padding: 12px 22px;
          margin-top: 6px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 18px;
          align-self: center;
          min-width: 140px;
        }

        .popupSubmit:hover {
          background: #0056b3;
        }

        .popupClose {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 32px;
          height: 32px;
          border: none;
          background: radial-gradient(circle at 30% 30%, #4f80ff, #1b3d84);
          border-radius: 8px;
          font-size: 21px;
          font-weight: bold;
          color: #fff;
          cursor: pointer;
          box-shadow: 0 6px 18px rgba(10, 15, 40, 0.35);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .popupClose:hover {
          filter: brightness(1.05);
        }

        .error {
          color: red;
          font-size: 14px;
          margin-top: 8px;
        }

        @media (max-width: 600px) {
          .popup {
            align-items: center;
            justify-content: center;
            padding: 12px;
          }

          .popupContent {
            width: min(390px, 100%);
            min-height: auto;
            max-height: calc(100dvh - 24px);
            border-radius: 14px;
            justify-content: flex-start;
            align-items: center;
            padding: 72px 14px 20px;
            gap: 10px;
          }

          .popupContent h3 {
            font-size: 38px;
          }

          .popupLogo {
            width: 110px;
          }

          .popupBody {
            width: min(360px, 100%);
          }

          .popupContent input {
            font-size: 16px;
            margin: 8px 0;
          }

          .popupClose {
            top: 12px;
            right: 12px;
            left: auto;
            transform: none;
          }

          .popupSubmit {
            width: min(360px, 100%);
          }
        }
      `}</style>
        </div>
    );
}
